import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { Bot, Loader2, LogOut, Settings2, Smile } from 'lucide-react';
import { api } from '@/lib/api';
import { useBotProfile, useGuilds } from '@/lib/hooks';
import { RUN_LABEL, runLink, runPercent, useRuns } from '@/lib/runs';
import type { SessionUser } from '@/lib/types';
import { cn } from '@/lib/utils';
import { BotStatusIndicator } from './BotStatusWidget';
import { RestartButton } from './RestartButton';
import { ThemeToggle } from './ThemeToggle';
import { Tooltip } from './Tooltip';

/**
 * Discord-style app rail (64px): home (+ progress ring while a backup run is
 * going), one icon per server (click = switch), global pages, and bot status
 * / restart / theme / profile at the bottom.
 * Labels live in tooltips so the rail stays narrow.
 */
export function AppRail({ user, isAdmin }: { user: SessionUser; isAdmin: boolean }) {
  const { pathname } = useLocation();
  const guilds = useGuilds(true);
  const bot = useBotProfile();

  return (
    <aside className="glass-menu flex w-16 shrink-0 flex-col items-center border-r border-border/50 py-3">
      <RailItem
        to="/"
        label={bot.data ? `${bot.data.name} · Server-Übersicht` : 'Server-Übersicht'}
        active={pathname === '/'}
        variant="server"
      >
        {bot.data?.avatarUrl ? (
          <img src={bot.data.avatarUrl} alt="" className="h-full w-full object-cover" />
        ) : (
          <Bot className="h-5 w-5" />
        )}
      </RailItem>
      <RunsIndicator isAdmin={isAdmin} />
      <RailDivider />

      {/* Server switcher — scrolls on its own when there are many servers. */}
      <nav className="flex w-full flex-1 flex-col items-center gap-2 overflow-y-auto overflow-x-hidden py-1 [scrollbar-width:none]">
        {(guilds.data?.guilds ?? []).map((g) => (
          <RailItem
            key={g.id}
            to={`/server/${g.id}`}
            label={g.name}
            active={pathname === `/server/${g.id}` || pathname.startsWith(`/server/${g.id}/`)}
            variant="server"
          >
            {g.iconUrl ? (
              <img src={g.iconUrl} alt="" className="h-full w-full object-cover" />
            ) : (
              <span className="text-xs font-semibold">{initials(g.name)}</span>
            )}
          </RailItem>
        ))}
      </nav>

      <RailDivider />
      <div className="flex w-full flex-col items-center gap-1">
        <RailItem to="/emojis" label="Emojis" active={pathname.startsWith('/emojis')}>
          <Smile className="h-5 w-5" />
        </RailItem>
        {isAdmin && (
          <RailItem
            to="/admin"
            label="Admin-Panel"
            active={['/admin', '/access', '/logs'].some((p) => pathname.startsWith(p))}
          >
            <Settings2 className="h-5 w-5" />
          </RailItem>
        )}
        <RailDivider />
        <BotStatusIndicator />
        <RestartButton visible={isAdmin} />
        <ThemeToggle />
        <UserMenu user={user} isAdmin={isAdmin} />
      </div>
    </aside>
  );
}

function RailItem({
  to,
  label,
  active,
  variant = 'nav',
  children,
}: {
  to: string;
  label: string;
  active: boolean;
  /** `server`: filled circle that turns into a rounded square when active (Discord). */
  variant?: 'server' | 'nav';
  children: ReactNode;
}) {
  return (
    <div className="group relative flex w-full justify-center">
      {/* Left pill: tall when active, short preview on hover. */}
      <span
        className={cn(
          'absolute left-0 top-1/2 w-1 -translate-y-1/2 rounded-r-full bg-foreground transition-all',
          active ? 'h-8' : 'h-0 group-hover:h-4'
        )}
      />
      <Tooltip label={label}>
        <Link
          to={to}
          aria-label={label}
          aria-current={active ? 'page' : undefined}
          className={cn(
            'flex h-10 w-10 items-center justify-center overflow-hidden transition-all',
            variant === 'server'
              ? active
                ? 'rounded-xl bg-primary text-primary-foreground'
                : 'rounded-full bg-muted text-foreground/80 hover:rounded-xl hover:bg-primary/80 hover:text-primary-foreground'
              : active
                ? 'rounded-xl bg-primary/15 text-primary'
                : 'rounded-full text-muted-foreground hover:bg-accent hover:text-foreground'
          )}
        >
          {children}
        </Link>
      </Tooltip>
    </div>
  );
}

/**
 * Ring with the percent of a running backup / restore / clone — only while
 * one runs. Click → its card (Backups page or Admin-Panel clone).
 */
function RunsIndicator({ isAdmin }: { isAdmin: boolean }) {
  const runs = useRuns();
  const guilds = useGuilds(true);
  const running = (runs.data ?? []).filter((r) => r.status === 'running');
  if (running.length === 0) return null;
  const run = running[0];
  const percent = runPercent(run);
  const server = guilds.data?.guilds.find((g) => g.id === run.guildId)?.name;
  const label =
    running.length > 1
      ? `${running.length} Vorgänge laufen`
      : `${RUN_LABEL[run.action].running}${server ? ` · ${server}` : ''}${percent !== null ? ` · ${percent} %` : ''}`;
  return (
    <div className="mt-2 w-full">
      <RailItem to={runLink(run, isAdmin)} label={label} active={false}>
        {percent === null ? (
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
        ) : (
          <span className="relative flex h-full w-full items-center justify-center text-primary">
            <svg viewBox="0 0 36 36" className="absolute inset-0 h-full w-full -rotate-90" aria-hidden>
              <circle cx="18" cy="18" r="15" fill="none" stroke="currentColor" strokeOpacity="0.2" strokeWidth="3" />
              <circle
                cx="18"
                cy="18"
                r="15"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeDasharray={`${(percent / 100) * 94.25} 94.25`}
                className="transition-[stroke-dasharray] duration-500"
              />
            </svg>
            <span className="text-[10px] font-semibold tabular-nums text-foreground">{percent}</span>
          </span>
        )}
      </RailItem>
    </div>
  );
}

function RailDivider() {
  return <div className="my-2 h-px w-8 shrink-0 bg-border" />;
}

/** Avatar → small menu (name, role, logout), opening to the right, bottom-aligned. */
function UserMenu({ user, isAdmin }: { user: SessionUser; isAdmin: boolean }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ left: 0, bottom: 0 });

  useEffect(() => {
    if (!open) return;
    const r = triggerRef.current?.getBoundingClientRect();
    if (r) setPos({ left: r.right + 10, bottom: window.innerHeight - r.bottom });
    const onDown = (e: MouseEvent) => {
      if (!triggerRef.current?.contains(e.target as Node) && !menuRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  return (
    <>
      <Tooltip label={user.username} disabled={open}>
        <button
          ref={triggerRef}
          type="button"
          aria-label="Profil"
          onClick={() => setOpen((o) => !o)}
          className="mt-1 flex h-10 w-10 items-center justify-center overflow-hidden rounded-full bg-muted text-xs font-semibold ring-offset-2 ring-offset-card transition hover:ring-2 hover:ring-primary/60"
        >
          {user.avatarUrl ? <img src={user.avatarUrl} alt="" className="h-full w-full object-cover" /> : initials(user.username)}
        </button>
      </Tooltip>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            className="glass-float fixed z-[100] w-56 rounded-md border bg-card p-3 shadow-2xl"
            style={{ left: pos.left, bottom: pos.bottom }}
          >
            <div className="truncate text-sm font-medium">{user.username}</div>
            <div className="mb-3 text-xs text-muted-foreground">{isAdmin ? 'Admin' : 'Mitglied'}</div>
            <button
              type="button"
              className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent"
              onClick={async () => {
                await api('/api/auth/logout', { method: 'POST' });
                window.location.href = '/login';
              }}
            >
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>,
          document.body
        )}
    </>
  );
}

function initials(name: string) {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .map((w) => w[0])
      .join('')
      .slice(0, 3)
      .toUpperCase() || '?'
  );
}
